/**
 * Sarvam STT/TTS client for conversational voice mode.
 *
 * Segment V1.4: always proxies through this app's own backend at
 * `${REACT_APP_API_BASE_URL}/voice/{stt,tts}` (the kgpt-backend routes —
 * hardened in V1.4 with a pooled connection and retry/backoff on the
 * Sarvam side). There used to be a second "direct to Sarvam" mode for local
 * dev, gated on REACT_APP_SARVAM_API_KEY — removed, not just unused: Sarvam
 * blocks direct browser calls (CORS), so that path never actually worked
 * outside whatever gap let it *look* like it did, and a real API key has no
 * business sitting in a browser bundle regardless. The key now lives only
 * in the backend and never reaches the browser.
 */

const TTS_SPEAKER_DEFAULT = "ritu";

export interface SttResult {
  transcript: string;
  language: string;
  /** 0-1 confidence in the detected language, when the backend supplies one
   * (Sarvam's own STT response does not document a confidence field, so this
   * is read defensively and defaults to 1 — i.e. "trust the detection" —
   * when absent. See CONVENTIONS.md note. */
  probability: number;
}

export interface TtsResult {
  bytes: ArrayBuffer;
  mimeType: string;
}

function sttUrl(): string {
  return `${process.env.REACT_APP_API_BASE_URL || ""}/voice/stt`;
}

function ttsUrl(): string {
  return `${process.env.REACT_APP_API_BASE_URL || ""}/voice/tts`;
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

/** Transcribe a recorded utterance via the backend's /voice/stt proxy. */
export async function stt(blob: Blob, signal?: AbortSignal): Promise<SttResult> {
  // Sarvam rejects "audio/webm;codecs=opus", accepts "audio/webm" — strip
  // the codec parameter (harmless if the backend doesn't care either way).
  const strippedType = (blob.type || "audio/webm").split(";")[0];
  const audioBlob = blob.type === strippedType ? blob : blob.slice(0, blob.size, strippedType);

  const formData = new FormData();
  formData.append("file", audioBlob, "audio.webm");

  const resp = await fetch(sttUrl(), { method: "POST", body: formData, signal });
  if (!resp.ok) {
    throw new Error(`Voice transcription failed (${resp.status})`);
  }
  const json = await resp.json();
  const results = json.results || {};
  return {
    transcript: ((results.transcript as string) || "").trim(),
    language: (results.language as string) || "unknown",
    probability: (results.probability as number) ?? 1,
  };
}

/** Synthesize speech for one chunk of text via the backend's /voice/tts proxy. */
export async function tts(
  text: string,
  lang: string,
  opts?: { speaker?: string; signal?: AbortSignal }
): Promise<TtsResult> {
  const speaker = opts?.speaker || TTS_SPEAKER_DEFAULT;

  const resp = await fetch(ttsUrl(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text, language: lang, speaker }),
    signal: opts?.signal,
  });
  if (!resp.ok) throw new Error(`Voice synthesis failed (${resp.status})`);
  const json = await resp.json();
  const results = json.results || {};
  if (!results.audio_base64) throw new Error("Voice synthesis returned no audio");
  return {
    bytes: base64ToArrayBuffer(results.audio_base64 as string),
    mimeType: (results.mime_type as string) || "audio/wav",
  };
}

/** Trivial TTS call to warm the connection. Fire-and-forget — errors are
 * swallowed, this is a latency optimization, never a correctness path. Call
 * it in parallel with stt() the moment a turn ends.
 * A bare "." is rejected by Sarvam (400, confirmed against the real API) —
 * it wants actual speakable text, not punctuation alone. */
export function prewarm(lang: string = "en-IN"): void {
  tts("hi", lang).catch(() => {
    /* best-effort only */
  });
}
